import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ChevronLeft, MessageCircle } from 'lucide-react';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useOpenInCasePartner } from '@/components/case-partner/hooks/useOpenInCasePartner';
import { buildHighlightGroups } from '@/components/pdf/hooks/highlight-utils';
import type { HighlightGroup } from '@/components/pdf/types/highlight-types';
import Loading from '@/components/shared/Loading';
import { OfficePreviewUnavailable } from '@/components/shared/OfficePreviewUnavailable';
import { useCitationTarget } from '@/hooks/useCitationTarget';
import { backendClient } from '@/lib/api-client';
import { downloadFile } from '@/lib/utils';
import { getFileExtension, isImageFileName, isPowerPointFileName, isSpreadsheetFileName, isTextFileName, isWordFileName } from '@/utils/file-utils';

const PDFViewer = lazy(() => import('@/components/pdf/PDFViewer'));
const WordDocViewer = lazy(() => import('../WordDocViewer'));
const EmailViewer = lazy(() => import('../email-viewer'));
const ExcelViewer = lazy(() => import('@/components/excel/ExcelViewer'));
const ImageViewer = lazy(() => import('@/components/image/ImageViewer'));
const TextFileViewer = lazy(() => import('@/components/text/TextFileViewer'));

const ViewerFallback = () => (
  <div className="flex items-center justify-center h-full">
    <Loading />
  </div>
);

const FileViewer = () => {
  const navigate = useNavigate();
  const openInCasePartner = useOpenInCasePartner();
  const { slug, fileId } = useParams({ from: '/matter/$slug/f/$fileId' });
  const {
    from,
    taskId,
    chunk,
    segment,
    page,
    cellRange,
  } = useSearch({ from: '/matter/$slug/f/$fileId' });
  const [fileUrl, setFileUrl] = useState<string>('');
  const [fileData, setFileData] = useState<{
    fileName: string;
    displayName: string;
    fileType: string;
    contentType: string;
    convertedToPdf?: boolean;
    folderPath?: string;
    documentGuid: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [taskHighlights, setTaskHighlights] = useState<Array<{ text: string; taskId: string }>>([]);
  const chunkId = chunk ? Number.parseInt(chunk, 10) : undefined;
  const segmentIndex = segment ? Number.parseInt(segment, 10) : undefined;
  const pageNumber = page ? Number.parseInt(page, 10) : undefined;
  const citationTarget = useCitationTarget({
    slug,
    documentGuid: fileData?.documentGuid,
    chunkId: Number.isFinite(chunkId) ? chunkId : undefined,
    segmentIndex: Number.isFinite(segmentIndex) ? segmentIndex : undefined,
    cellRange,
    initialPageNumber: Number.isFinite(pageNumber) ? pageNumber : undefined,
  });

  const navigateBackToSource = () => {
    if (from === 'bundle') {
      navigate({ to: '/matter/$slug/case-files', params: { slug } });
      return;
    }

    if (from === 'overview') {
      navigate({ to: '/matter/$slug/overview', params: { slug } });
      return;
    }

    if (from === 'case-files/correspondence') {
      navigate({ to: '/matter/$slug/case-files/correspondence', params: { slug } });
      return;
    }

    if (from === 'case-files/all-files') {
      navigate({ to: '/matter/$slug/case-files/all-files', params: { slug } });
      return;
    }

    const correspondenceFolderMatch = from?.match(/^case-files\/correspondence\/folder\/([^/]+)$/);
    if (correspondenceFolderMatch) {
      navigate({
        to: '/matter/$slug/case-files/correspondence/folder/$folderGuid',
        params: { slug, folderGuid: correspondenceFolderMatch[1] },
      });
      return;
    }

    const folderMatch = from?.match(/^case-files\/folder\/([^/]+)$/);
    if (folderMatch) {
      navigate({
        to: '/matter/$slug/case-files/folder/$folderGuid',
        params: { slug, folderGuid: folderMatch[1] },
      });
      return;
    }

    navigate({ to: '/matter/$slug/case-files', params: { slug } });
  };

  // Fetch all matched_text from tasks linked to this document
  // This runs whether or not taskId is provided to ensure the toggle button appears
  useEffect(() => {
    if (slug && fileId && fileData?.documentGuid) {
      const fetchAllDocumentHighlights = async () => {
        try {
          const response = await backendClient.get(`/deadlines/${slug}`);
          const allTasks = response.data.deadlines;

          // Find all tasks that have this document in their source_documents
          const matchedTexts: string[] = [];
          const taskMapping: Array<{ text: string; taskId: string }> = [];
          let primaryHighlight: string | undefined;

          for (const task of allTasks) {
            if (task.source_documents && task.source_documents.length > 0) {
              const sourceDoc = task.source_documents.find(
                (doc: any) => doc.document_guid === fileData.documentGuid
              );

              if (sourceDoc?.matched_text) {
                matchedTexts.push(sourceDoc.matched_text);
                taskMapping.push({ text: sourceDoc.matched_text, taskId: task.id });

                // If taskId is provided and this is the primary task, remember it
                if (taskId && task.id === taskId) {
                  primaryHighlight = sourceDoc.matched_text;
                }
              }
            }
          }

          if (matchedTexts.length > 0) {
            // Reorder task mapping so primary task is first (for key highlight detection)
            if (taskId && primaryHighlight) {
              const primaryMapping = taskMapping.find(m => m.text === primaryHighlight);
              const otherMappings = taskMapping.filter(m => m.text !== primaryHighlight);
              setTaskHighlights(primaryMapping ? [primaryMapping, ...otherMappings] : taskMapping);
            } else {
              setTaskHighlights(taskMapping);
            }
          }
        } catch (error) {
          console.error('Failed to fetch task highlights:', error);
        }
      };

      fetchAllDocumentHighlights();
    }
  }, [taskId, slug, fileId, fileData?.documentGuid]);

  const highlightGroups: HighlightGroup[] = useMemo(
    () => buildHighlightGroups({
      taskHighlights,
      keyTaskId: typeof taskId === 'string' ? taskId : undefined,
      showDeadlinesInitially: !!taskId,
    }),
    [taskHighlights, taskId],
  );

  // Function to determine file type from file name or extension
  const getFileType = (fileName: string): 'pdf' | 'msg' | 'word' | 'powerpoint' | 'excel' | 'image' | 'text' | null => {
    if (!fileName) return null;
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'msg' || extension === 'eml') {
      return 'msg';
    } else if (isWordFileName(fileName)) {
      return 'word';
    } else if (isPowerPointFileName(fileName)) {
      return 'powerpoint';
    } else if (isSpreadsheetFileName(fileName)) {
      return 'excel';
    } else if (isImageFileName(fileName)) {
      return 'image';
    } else if (isTextFileName(fileName)) {
      return 'text';
    } else {
      return 'pdf';
    }
  };

  // Fetch the file content from the backend
  useEffect(() => {
    if (!fileId || !slug) return;

    const fetchFileContent = async () => {
      try {
        setIsLoading(true);

        // Try to fetch file from /get-file endpoint using FileReference GUID
        // This provides folder context for the side panel
        // Works for PDFs and Word docs (stored in DATA container)
        // But returns 404 for emails (stored in email-file-data container)
        const response = await backendClient.get(`/correspondence/${slug}/get-file`, {
          params: { file_reference_guid: fileId }
        });

        if (!response.data.signedUrl) {
          throw new Error("Invalid response format from API");
        }

        // Successfully got file URL
        setFileUrl(response.data.signedUrl);
        setFileData(response.data);

      } catch (error: any) {
        console.error("Error fetching file content:", error);
        toast.error("Failed to load file. The file may not exist or you may not have permission to access it.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchFileContent();
  }, [fileId, slug]);

  const handleDownload = async () => {
    if (!slug || !fileId) return;
    try {
      const response = await backendClient.get(`/correspondence/${slug}/get-file`, {
        params: { file_reference_guid: fileId, download: true }
      });
      if (!response.data?.signedUrl || !response.data?.fileName) {
        throw new Error('No signed URL returned from server');
      }
      await downloadFile(response.data.signedUrl, response.data.fileName);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Could not download the file.');
    }
  };

  const renderDocumentViewer = () => {
    return (
      <div className="bg-white rounded-xl shadow-xs border border-slate-200 h-full flex flex-col">
        <div className="px-4 py-3 border-b border-slate-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={navigateBackToSource}
                className="p-1 hover:bg-slate-100 rounded-lg transition-colors duration-200"
              >
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-slate-800">{fileData?.displayName}</h1>
              </div>
            </div>
            <button
              type="button"
              onClick={() => openInCasePartner(slug, {
                guid: fileData?.documentGuid,
                displayName: fileData?.displayName,
                fileName: fileData?.fileName,
              })}
              className="flex items-center gap-1.5 px-3 py-1.5 mr-28 text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors duration-200"
              title="Open in Case Partner"
              disabled={!fileData?.documentGuid}
            >
              <MessageCircle className="w-4 h-4" />
              <span>Open in Case Partner</span>
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="h-full flex items-center justify-center">
              <Loading />
            </div>
          ) : !fileData ? null : getFileType(fileData.fileName) === 'msg' ? (
            <Suspense fallback={<ViewerFallback />}>
              <EmailViewer
                documentGuid={fileId}
                taskId={taskId}
              />
            </Suspense>
          ) : getFileType(fileData.fileName) === 'word' && !fileData.convertedToPdf ? (
            getFileExtension(fileData.fileName).toLowerCase() === '.doc' ? (
              <OfficePreviewUnavailable onDownload={handleDownload} />
            ) : (
              <Suspense fallback={<ViewerFallback />}>
                <WordDocViewer fileUrl={fileUrl} />
              </Suspense>
            )
          ) : getFileType(fileData.fileName) === 'powerpoint' && !fileData.convertedToPdf ? (
            <OfficePreviewUnavailable onDownload={handleDownload} />
          ) : getFileType(fileData.fileName) === 'excel' ? (
            <Suspense fallback={<ViewerFallback />}>
              <ExcelViewer
                matterGuid={slug}
                fileReferenceGuid={fileId}
                documentGuid={fileData.documentGuid}
                fileName={fileData.fileName}
                citationLocator={citationTarget.excelLocator}
                viewMode="fullscreen"
              />
            </Suspense>
          ) : getFileType(fileData.fileName) === 'image' ? (
            <Suspense fallback={<ViewerFallback />}>
              <ImageViewer
                fileUrl={fileUrl}
                fileName={fileData.fileName}
                onDownload={handleDownload}
              />
            </Suspense>
          ) : getFileType(fileData.fileName) === 'text' ? (
            <Suspense fallback={<ViewerFallback />}>
              <TextFileViewer
                fileUrl={fileUrl}
                fileName={fileData.fileName}
                citationRange={citationTarget.textLocator}
                scrollOffset={citationTarget.chunkStartOffset}
              />
            </Suspense>
          ) : (
            <Suspense fallback={<ViewerFallback />}>
              <PDFViewer
                fileUrl={fileUrl}
                highlightGroups={citationTarget.highlightGroups.length > 0
                  ? citationTarget.highlightGroups
                  : highlightGroups}
                slug={slug}
                onDownload={handleDownload}
                fileName={fileData?.fileName}
                sourceDocumentGuid={fileData?.documentGuid}
                initialPage={citationTarget.pageNumber}
                viewMode='fullscreen'
              />
            </Suspense>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <title>{fileData?.displayName || 'Crimson - Document Viewer'}</title>
      <div className="h-full">
        {renderDocumentViewer()}
      </div>
    </>
  );
};
export default FileViewer;
